// store/index.ts
import {
  Action,
  combineReducers,
  configureStore,
  isRejectedWithValue,
  Middleware,
  MiddlewareAPI,
  ThunkAction,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { createBrowserHistory } from "history";

import { apiSlice } from "./apiSlice";
import { makePatchSpyMiddleware } from "./app/patchSpyMiddleware";
import { trpcSlice } from "./trpcSlice";

// Slices (vanliga importer – inte require)
import { adminReducer } from "./app/reducer";
import { mcbimSlice } from "./app/routes/admin/mcbim/mcBimSlice";
import { batchJobSlice } from "./app/routes/batchjob/batchJobSlice";
import checklistReducer from "./app/routes/checklist/checklistSlice";
import { issueSlice } from "./app/routes/issuesApp/issueSlice";
import { meetingsSlice } from "./app/routes/meeting/meetingSlice";
import { projectDashboardSlice } from "./app/routes/projectdashboard/projectDashboardSlice";
import { reportSlice } from "./app/routes/report/reportSlice";
import { stateReducer } from "./app/routes/stats/statsSlice";
import { agGridStateSlice } from "./global/agGridStateSlice";
import { globalSlice } from "./global/globalSlice";

export const history = createBrowserHistory();

// ✅ Använd combineReducers för stabil typinferens
const rootReducer = combineReducers({
  adminReducer,
  agGridReducer: agGridStateSlice.reducer,
  globalReducer: globalSlice.reducer,
  checklistReducer,
  batchJobReducer: batchJobSlice.reducer,
  reportReducer: reportSlice.reducer,
  mcBimReducer: mcbimSlice.reducer,
  projectDashboardReducer: projectDashboardSlice.reducer,
  meetingReducer: meetingsSlice.reducer,
  issueReducerNew: issueSlice.reducer,
  statsReducer: stateReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  [trpcSlice.reducerPath]: trpcSlice.reducer,
});

// Root state-typ från reducer
export type IRootState = ReturnType<typeof rootReducer>;

/** Enkel logger för rejected actions (valfritt) */
export const rtkQueryErrorLogger: Middleware = (_api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    // console.warn("Rejected action:", action);
  }
  return next(action);
};

const __DEV__ = process.env.NODE_ENV !== "production";

// Typ för en generisk RTK Query-slice
type ApiSlice = {
  reducerPath: string;
  // Lägg till andra egenskaper om nödvändigt
};

export const makeStore = (preloadedState?: Partial<IRootState>) => {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => {
      const base = getDefaultMiddleware().concat(rtkQueryErrorLogger, apiSlice.middleware, trpcSlice.middleware);

      // Dev-only: spionera på queryResultPatched för både apiSlice och trpcSlice
      return __DEV__ ? base.concat(makePatchSpyMiddleware([apiSlice, trpcSlice])) : base;
    },
    preloadedState,
    // devTools: __DEV__,
  });

  setupListeners(store.dispatch);
  return store;
};

export const store = makeStore();
export type AppStore = typeof store;
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ThunkReturnType = void> = ThunkAction<ThunkReturnType, IRootState, unknown, Action>;
